import {
  ShareInfoFragment,
  useVersionDeletedSubscription,
  ShareInfoFragmentDoc,
  ShareLatestStarredVersionFragment,
  ShareLatestStarredVersionFragmentDoc,
  VersionFragment,
  VersionFragmentDoc,
} from '@sketch/gql-types'
import { ClientOrCache, dataIdFromObject } from '@sketch/graphql-cache'
import { useEventDispatch } from '@sketch/utils'

import {
  shouldReduceTotalCount,
  wasDeletionInitiatedByThisClient,
} from './deletedVersionsTracking'
import { updateVersionHistory } from './updateVersionHistory'
import { ErrorHandler } from '@sketch/tracing'
import produce from 'immer'

declare module '@sketch/utils' {
  export interface EventsMap {
    versionDeleted: {
      /**
       * shortId of previous latest version
       */
      versionShortId: string
      share: ShareInfoFragment
      versionKind: string
    }
  }
}

const updateVersionFragment = (
  apollo: ClientOrCache,
  versionShortId: string,
  updater: (version: VersionFragment) => void
) => {
  const id = dataIdFromObject({
    __typename: 'Version',
    shortId: versionShortId,
  })!

  const version = apollo.readFragment<VersionFragment>({
    fragment: VersionFragmentDoc,
    id,
    fragmentName: 'Version',
  })

  if (!version) {
    return
  }

  const data = produce(version, version => updater(version))

  apollo.writeFragment<VersionFragment>({
    fragment: VersionFragmentDoc,
    id,
    fragmentName: 'Version',
    data,
  })
}

export const useVersionDeleted = () => {
  const onVersionDeleted = useEventDispatch('versionDeleted')

  useVersionDeletedSubscription({
    onData: ({ client, data: { loading, data, error } }) => {
      if (!data || error || loading) {
        ErrorHandler.shouldNeverHappen(
          'versionDeleted event should contain valid data'
        )
        return
      }

      const { share, versionDeleted } = data.versionDeleted

      updateVersionHistory({
        client,
        variables: { shareIdentifier: share.identifier },
        update: share => {
          if (shouldReduceTotalCount(versionDeleted.identifier)) {
            share.availableVersions.meta.totalCount--
            share.versionHistory.meta.totalCount--
          }
          share.versionHistory.entries = share.versionHistory.entries.filter(
            entry => entry.identifier !== versionDeleted.identifier
          )
        },
      })

      const versionShortId = versionDeleted.shortId
      const versionKind = versionDeleted.kind

      if (versionShortId) {
        updateVersionFragment(client, versionShortId, version => {
          version.deletedAt = versionDeleted.deletedAtDate
        })

        if (!wasDeletionInitiatedByThisClient(versionDeleted.identifier)) {
          onVersionDeleted({ versionShortId, share, versionKind })
        }
      }

      client.writeFragment<ShareInfoFragment>({
        data: share,
        fragment: ShareInfoFragmentDoc,
        id: dataIdFromObject(share)!,
        fragmentName: 'ShareInfo',
      })

      // Synch the latest starred version with the share type
      client.writeFragment<ShareLatestStarredVersionFragment>({
        data: share,
        fragment: ShareLatestStarredVersionFragmentDoc,
        id: dataIdFromObject(share)!,
        fragmentName: 'ShareLatestStarredVersion',
      })

      if (versionShortId) {
        const cache = client.cache
        cache.evict({
          id: cache.identify({
            __typename: 'Version',
            shortId: versionShortId,
          }),
        })
      }
    },
    fetchPolicy: 'no-cache',
  })
}
